import AppContext from 'App/AppContext';
import { IPAlignment, IPImage, IPImageLoadState, IPText } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { globalTextStylesError } from 'globalStyles';
import { useContext, useEffect, useState } from 'react';
import { graphGetSitePageImage, graphGetSitePageImageUri } from 'services/Graph/graphServicePage';
import { ISitePage, IStandardWebPart } from 'services/Graph/SharepointInterfaces';
import AppError from 'utils/appError';
import { imgBgErrorPlaceholder, ImgBgState } from '../SharePointPageRenderHelpers';

interface ISharePointImageWebPartProps {
  webPart: IStandardWebPart;
  page: ISitePage;
}

export const SharePointImageWebPart = (props: ISharePointImageWebPartProps) => {
  const appContext = useContext(AppContext);
  const [bgImageUri, setBgImageUri] = useState<string | undefined>(undefined);
  const [imgBgState, setImgBgState] = useState<ImgBgState>(ImgBgState.Initializing);
  const [imgError, setImgError] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (props.webPart && props.page) {
      setImgError(undefined);
      const uri = graphGetSitePageImageUri(props.page, props.webPart);
      if (uri) {
        setBgImageUri(uri);
        setImgBgState(ImgBgState.LoadingImage);
      } else {
        handleError();
      }
    }
  }, [props.webPart, props.page]);

  const loadBgImageAsync = async (page: ISitePage, webPart: IStandardWebPart) => {
    try {
      const imageSrcWp = await graphGetSitePageImage(appContext, page, webPart);
      setBgImageUri(imageSrcWp);
    } catch (err) {
      const appErr = AppError.fromGraphError(err);
      setImgError(appErr.message);
      handleError();
    }
  };

  const handleError = () => {
    setImgBgState(ImgBgState.Error);
    setBgImageUri(imgBgErrorPlaceholder);
  };

  const handleStateChange = (state: IPImageLoadState) => {
    switch (state) {
      case IPImageLoadState.notLoaded:
        setImgBgState(ImgBgState.LoadingImage);
        break;
      case IPImageLoadState.error:
        switch (imgBgState) {
          case ImgBgState.LoadingImage:
            setImgBgState(ImgBgState.LoadingBlob);
            loadBgImageAsync(props.page, props.webPart);
            break;
          case ImgBgState.LoadingBlob:
            setImgBgState(ImgBgState.Error);
            setBgImageUri(imgBgErrorPlaceholder);
            break;
        }
        break;
      case IPImageLoadState.loaded:
        setImgBgState(ImgBgState.Ready);
        break;
      default:
        break;
    }
  };

  //
  // Main render
  //
  if (!props.webPart?.data) {
    return null;
  }

  // Width. Height is not specified, so it will be auto-scaled
  const shape = props.webPart.data.properties?.imageShapeData;
  let resizeDesiredWidth = props.webPart.data.properties?.resizeDesiredWidth;
  if (!resizeDesiredWidth || resizeDesiredWidth <= 0) {
    // If no width is specified, use the share width, image width or default to 100%
    resizeDesiredWidth = shape?.imageShapeData?.width ?? props.webPart.data.properties?.imgWidth ?? '100%';
  }

  // Determine alignment
  const align = props.webPart.data.properties?.alignment || ('Left' as string);
  let imageAlignment: IPAlignment | undefined = 'start';
  if (align.toLowerCase() === 'center') imageAlignment = 'center';
  else if (align.toLowerCase() === 'right') imageAlignment = 'end';

  // Transform properties
  const transformData = shape?.imageTransformData ?? {};
  const zoom = transformData.zoom ?? 1;
  const translateX = transformData.translateX ?? 0; // in px
  const translateY = transformData.translateY ?? 0; // in px
  const imgStyle: React.CSSProperties = {
    transform:
      zoom !== 1 || translateX || translateY ? `translate(${translateX}px, ${translateY}px) scale(${zoom})` : undefined,
    transformOrigin: 'top left',
  };

  return (
    <IPStack horizontalAlign={imageAlignment}>
      <IPStack.Item>
        <IPImage
          src={bgImageUri}
          width={resizeDesiredWidth}
          styles={{ image: { imgStyle } }}
          onLoadingStateChange={(ev) => handleStateChange(ev)}
        />
      </IPStack.Item>
      {imgBgState === ImgBgState.Error && (
        <IPStack.Item>
          <IPText variant="small" styles={globalTextStylesError}>
            {imgError || 'Error loading image'}
          </IPText>
        </IPStack.Item>
      )}
    </IPStack>
  );
};
